import { useEffect, useRef, useState } from "react";
import { Send, Sparkles, Bot, User, Square, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { newId } from "../lib/api";
import { useHardware } from "../context/HardwareContext";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { LocalAiGuide } from "../components/assistant/LocalAiGuide";
import { ModelLoader } from "../components/assistant/ModelLoader";
import { LOCAL_MODELS, checkWebGPU, getLoadedModel, loadModel, recommendLocalModel, streamChat, stopChat } from "../lib/localLlm";
import { GuidedAdvisor } from "../components/assistant/GuidedAdvisor";
import { ExternalLlmPanel } from "../components/assistant/ExternalLlmPanel";
import { FreeBanner, LocallyAiCard } from "../components/assistant/FreeBanner";
import { PROVIDERS, streamExternal } from "../lib/externalLlm";

function ModeTabs({ mode, setMode }) {
  const tabs = [["ai", "IA dans le navigateur"], ["external", "Mon Ollama / LM Studio"], ["guided", "Questionnaire guidé (sans IA)"]];
  return (
    <div className="flex gap-1 rounded-lg border border-[#1E2638] bg-[#0F141C] p-1" data-testid="assistant-mode-tabs">
      {tabs.map(([id, label]) => (
        <button key={id} onClick={() => setMode(id)} data-testid={`assistant-mode-${id}`}
          className={`flex-1 rounded-md px-3 py-2 text-sm transition-colors ${mode === id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-slate-200"}`}>
          {label}
        </button>
      ))}
    </div>
  );
}

const SUGGESTIONS = [
  "Je veux coder en Python, quel modèle avec mon matériel ?",
  "Quel est le meilleur modèle de raisonnement que je peux faire tourner ?",
  "Explique la différence entre Q4_K_M et Q8_0",
  "Recommande un modèle de vision pour mon GPU",
];

const CHAT_KEY = "llmetric_local_chat";
const MODEL_KEY = "llmetric_local_model";
const EXT_KEY = "llmetric_external_llm";

const systemPrompt = (hw) =>
  "Tu es l'assistant de llmetric, expert en LLM open source et en matériel. Aide l'utilisateur à choisir des modèles " +
  "(Llama, Mistral, Qwen, Gemma, Phi, DeepSeek) et une quantification (Q4_K_M, Q5_K_M, Q8_0) adaptés à son matériel. " +
  "Donne des recommandations concrètes, l'estimation de mémoire et la commande (ex: `ollama run llama3.1:8b`). " +
  "Réponds toujours en français, de façon concise. " +
  `Matériel : CPU ${hw.cpuCores} cœurs, RAM ${hw.ram} Go, GPU ${hw.gpu || "inconnu"}, VRAM ${hw.vram} Go, accélérateur ${hw.accelerator}.`;

function Message({ m, pending }) {
  const user = m.role === "user";
  return (
    <div className={`flex gap-3 ${user ? "flex-row-reverse" : ""}`}>
      <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${user ? "bg-accent" : "bg-primary text-primary-foreground"}`}>
        {user ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
      </div>
      <div data-testid={`assistant-message-${m.role}`} className={`max-w-[80%] whitespace-pre-wrap rounded-xl px-4 py-2.5 text-sm ${user ? "bg-primary text-primary-foreground" : "border border-[#1E2638] bg-[#080B10] text-slate-200"}`}>
        {m.content || (pending ? "…" : "")}
      </div>
    </div>
  );
}

export default function Assistant() {
  const { hardware } = useHardware();
  const [messages, setMessages] = useState(() => JSON.parse(localStorage.getItem(CHAT_KEY) || "[]"));
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [gpu, setGpu] = useState(null);
  const [modelId, setModelId] = useState(() => {
    const saved = localStorage.getItem(MODEL_KEY);
    return LOCAL_MODELS.some((m) => m.id === saved) ? saved : recommendLocalModel(hardware);
  });
  const recommended = recommendLocalModel(hardware);
  const [mode, setMode] = useState("ai");
  const [status, setStatus] = useState(() => (getLoadedModel() === modelId ? "ready" : "idle"));
  const [progress, setProgress] = useState({ value: 0, text: "" });
  const scrollRef = useRef(null);
  const abortRef = useRef(null);
  const [ext, setExt] = useState(() => ({
    provider: "ollama", base: PROVIDERS.ollama.base, model: "", models: [], connected: false,
    ...JSON.parse(localStorage.getItem(EXT_KEY) || "{}"),
  }));
  useEffect(() => {
    localStorage.setItem(EXT_KEY, JSON.stringify({ provider: ext.provider, base: ext.base, model: ext.model }));
  }, [ext.provider, ext.base, ext.model]);

  useEffect(() => {
    checkWebGPU().then((g) => {
      setGpu(g);
      if (!g.ok) setMode("guided");
    });
  }, []);
  useEffect(() => { localStorage.setItem(CHAT_KEY, JSON.stringify(messages.slice(-50))); }, [messages]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages]);
  useEffect(() => {
    localStorage.setItem(MODEL_KEY, modelId);
    setStatus(getLoadedModel() === modelId ? "ready" : "idle");
  }, [modelId]);

  const load = async () => {
    setStatus("loading");
    setProgress({ value: 0, text: "Préparation…" });
    try {
      await loadModel(modelId, (r) => setProgress({ value: r.progress || 0, text: r.text || "" }));
      setStatus("ready");
      toast.success("Modèle prêt, vous pouvez discuter");
    } catch (e) {
      setStatus("idle");
      toast.error(`Impossible de démarrer le modèle : ${e.message || e}. Essayez un modèle plus léger.`);
    }
  };

  const ready = mode === "external" ? ext.connected && !!ext.model : status === "ready";
  const stop = () => (mode === "external" ? abortRef.current?.abort() : stopChat());

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || busy || !ready) return;
    setInput("");
    setBusy(true);
    const history = [...messages, { id: newId(), role: "user", content }];
    const replyId = newId();
    setMessages([...history, { id: replyId, role: "assistant", content: "" }]);
    const update = (c) => setMessages((m) => [...m.slice(0, -1), { id: replyId, role: "assistant", content: c }]);
    try {
      const msgs = [{ role: "system", content: systemPrompt(hardware) }, ...history.slice(-8).map(({ role, content: c }) => ({ role, content: c }))];
      if (mode === "external") {
        abortRef.current = new AbortController();
        await streamExternal(ext.base, ext.model, msgs, update, abortRef.current.signal);
      } else {
        await streamChat(msgs, update);
      }
    } catch (e) {
      if (e.name !== "AbortError") update(`Erreur du modèle local : ${e.message || e}`);
    }
    setBusy(false);
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-6 md:px-8">
      <div className="mb-4 flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <Sparkles className="h-6 w-6 text-primary" />
          <div>
            <h1 className="font-head text-2xl font-extrabold tracking-tight">Assistant IA local</h1>
            <p className="font-mono2 text-xs text-muted-foreground">
              Gratuit · 100 % local · {hardware.cpuCores} cœurs · {hardware.ram} Go RAM · {hardware.vram} Go VRAM
            </p>
          </div>
        </div>
        {messages.length > 0 && (
          <Button variant="outline" size="sm" onClick={() => setMessages([])} disabled={busy} data-testid="assistant-reset-button" className="gap-1.5 text-xs">
            <RotateCcw className="h-3.5 w-3.5" /> Nouvelle conversation
          </Button>
        )}
      </div>

      <div className="space-y-4">
        <FreeBanner />
        <LocallyAiCard />
        <ModeTabs mode={mode} setMode={setMode} />
        {mode === "guided" ? <GuidedAdvisor /> : (<>
        {mode === "external" ? <ExternalLlmPanel ext={ext} setExt={setExt} /> : <ModelLoader modelId={modelId} setModelId={setModelId} status={status} progress={progress} onLoad={load} gpu={gpu} recommended={recommended} />}

        <div ref={scrollRef} className="h-[55vh] space-y-4 overflow-y-auto rounded-xl border border-[#1E2638] bg-[#0F141C] p-5" data-testid="assistant-chat">
          {messages.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <Bot className="mb-3 h-10 w-10 text-primary" />
              <p className="max-w-md text-sm text-muted-foreground">
                {ready ? "Posez une question sur les modèles adaptés à votre matériel." : mode === "external" ? "Connectez votre Ollama ou LM Studio ci-dessus pour commencer." : "Démarrez un modèle ci-dessus pour commencer à discuter."}
              </p>
              <div className="mt-5 grid w-full max-w-lg gap-2 sm:grid-cols-2">
                {SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => send(s)} disabled={!ready} data-testid="assistant-suggestion"
                    className="rounded-lg border border-[#1E2638] bg-[#080B10] p-3 text-left text-xs text-slate-300 transition-colors hover:border-primary hover:text-primary disabled:opacity-50">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((m, i) => <Message key={m.id || `legacy-${i}`} m={m} pending={busy && i === messages.length - 1} />)
          )}
        </div>

        <div className="flex items-end gap-2">
          <Textarea maxLength={2000} value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={ready ? "Votre question…" : mode === "external" ? "Connectez d'abord votre Ollama / LM Studio" : "Démarrez d'abord un modèle"} rows={1}
            data-testid="ai-assistant-chat-input" className="max-h-32 min-h-[44px] resize-none" />
          {busy ? (
            <Button onClick={stop} data-testid="ai-assistant-stop-button" size="icon" variant="outline" className="h-11 w-11 shrink-0" aria-label="Arrêter">
              <Square className="h-4 w-4" />
            </Button>
          ) : (
            <Button onClick={() => send()} disabled={!ready || !input.trim()} data-testid="ai-assistant-send-button" size="icon" className="h-11 w-11 shrink-0">
              <Send className="h-4 w-4" />
            </Button>
          )}
        </div>
        </>)}

        <LocalAiGuide />
      </div>
    </div>
  );
}
